// Keyterm biasing for Deepgram — the biggest accuracy lever for clinical
// dictation. Biases recognition toward ABA terminology (always-on) plus dynamic
// per-session terms (the client's name), which is what fixes the proper-noun /
// jargon misfires Web Speech made ("gestural"->"historial", "Xome"->"zomi").
//
// Model-aware: nova-3 uses `keyterm` (stronger, purpose-built, no intensifier);
// nova-2 uses `keywords` with an intensifier (`term:boost`). Switching the model
// automatically switches the param.

// Curated ABA / clinical glossary. Acronyms included so Deepgram emits them
// (DTT, FCT, SIB, SD...). Keep to genuinely domain-specific terms — over-stuffing
// generic words invites false positives.
export const ABA_KEYTERMS: string[] = [
  'ABA', 'DTT', 'discrete trial training', 'discrete trial',
  'FCT', 'functional communication training',
  'mand', 'tact', 'echoic', 'intraverbal', 'listener responding',
  'gestural prompt', 'verbal prompt', 'physical prompt', 'model prompt',
  'positional prompt', 'prompt hierarchy', 'prompt fading', 'independent response',
  'SD', 'discriminative stimulus', 'MO', 'motivating operation',
  'reinforcement', 'differential reinforcement', 'DRA', 'DRO', 'DRI',
  'extinction', 'token economy', 'pairing', 'behavior momentum',
  'behavior intervention plan', 'BIP', 'antecedent', 'consequence', 'ABC data',
  'functional behavior assessment', 'FBA',
  'stereotypy', 'elopement', 'self-injurious behavior', 'SIB', 'aggression', 'noncompliance',
  'generalization', 'maintenance', 'task analysis', 'chaining', 'shaping',
  'natural environment teaching', 'NET', 'pivotal response training', 'PRT',
  'BCBA', 'RBT', 'BCaBA',
];

// Expand dynamic terms (e.g. a full client name) into the phrase PLUS its
// individual words, so an uncommon first name ("Xome") is boosted on its own.
function expandDynamic(terms: string[]): string[] {
  const out = new Set<string>();
  for (const raw of terms) {
    const t = (raw || '').trim();
    if (!t) continue;
    out.add(t);
    for (const w of t.split(/\s+/)) if (w.length >= 2) out.add(w);
  }
  return [...out];
}

/**
 * Append keyterm/keywords params for the given Deepgram model. Mutates `params`.
 * @param dynamicTerms per-session terms (client name(s)) — boosted higher than glossary.
 */
export function appendKeyterms(
  params: URLSearchParams,
  model: string,
  dynamicTerms: string[] = [],
  boost: { glossary: number; dynamic: number } = { glossary: 2, dynamic: 3 }
): void {
  const isNova3 = model.startsWith('nova-3');
  const add = (term: string, b: number) => {
    const t = term.trim();
    if (!t) return;
    // URLSearchParams handles encoding (spaces, etc.).
    if (isNova3) params.append('keyterm', t);
    else params.append('keywords', `${t}:${b}`);
  };
  for (const t of ABA_KEYTERMS) add(t, boost.glossary);
  for (const t of expandDynamic(dynamicTerms)) add(t, boost.dynamic);
}
